import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { settingsApi, type NotificationSettings } from "@/api/settings";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

const sameSettings = (a: NotificationSettings, b: NotificationSettings) =>
  a.notify_on_indexer_failure === b.notify_on_indexer_failure &&
  a.notify_on_indexer_recovery === b.notify_on_indexer_recovery;

/**
 * Settings → Notifications (issue #40).
 *
 * Today the page exposes the two cross-connector toggles for the
 * indexer-health alert family that ships with #40. Per-connector
 * configuration (Discord/ntfy/webhook/Plex) is reachable through the
 * REST API at `/api/v1/notifications` until the broader Notifications
 * UI lands; this page is the bridging surface so users can mute the
 * disable / recovery alerts without touching SQL.
 */
export function Notifications() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const settingsQ = useQuery({
    queryKey: ["settings", "notifications"],
    queryFn: settingsApi.notifications.get,
  });

  const [draft, setDraft] = useState<NotificationSettings | null>(null);

  // Re-seed the draft once the query lands. We only re-seed when the
  // user has no unsaved edits — same dance as the Backups + NZB
  // settings pages.
  useEffect(() => {
    if (!settingsQ.data) return;
    if (draft && !sameSettings(draft, settingsQ.data)) return;
    setDraft(settingsQ.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQ.data]);

  const dirty = useMemo(
    () =>
      !!draft &&
      !!settingsQ.data &&
      !sameSettings(draft, settingsQ.data),
    [draft, settingsQ.data],
  );

  const saveMut = useMutation({
    mutationFn: (body: NotificationSettings) => settingsApi.notifications.set(body),
    onSuccess: (next) => {
      toast("Notification settings saved", "success");
      setDraft(next);
      qc.invalidateQueries({ queryKey: ["settings", "notifications"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const toggle = (key: keyof NotificationSettings) => {
    if (!draft) return;
    setDraft({ ...draft, [key]: !draft[key] });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Cross-connector toggles for event-driven notifications. Per-connector setup (Discord, ntfy, webhook, Plex) lives at /api/v1/notifications."
      />

      <section className="space-y-3 rounded border border-line bg-surface/40 px-4 py-4">
        <h2 className="text-sm font-medium uppercase tracking-wide text-foreground/70">
          Indexer health
        </h2>
        <p className="text-sm text-foreground/60">
          Fired when arr-in-one auto-disables an indexer after sustained
          failures, and again when a manual reset puts it back online.
        </p>

        <label className="flex items-start gap-3 rounded border border-line/60 bg-background/40 px-3 py-2">
          <Checkbox
            checked={draft?.notify_on_indexer_failure ?? true}
            disabled={!draft || settingsQ.isLoading}
            onChange={() => toggle("notify_on_indexer_failure")}
          />
          <div className="flex-1">
            <div className="text-sm">Notify on indexer auto-disable</div>
            <div className="text-xs text-foreground/50">
              Sends an "Indexer X disabled" warning to every enabled
              connector when the failure backoff layer trips.
            </div>
          </div>
        </label>

        <label className="flex items-start gap-3 rounded border border-line/60 bg-background/40 px-3 py-2">
          <Checkbox
            checked={draft?.notify_on_indexer_recovery ?? true}
            disabled={!draft || settingsQ.isLoading}
            onChange={() => toggle("notify_on_indexer_recovery")}
          />
          <div className="flex-1">
            <div className="text-sm">Notify on indexer recovery</div>
            <div className="text-xs text-foreground/50">
              Sends an "Indexer X recovered" confirmation after a manual
              re-enable / reset puts the indexer back in service.
            </div>
          </div>
        </label>
      </section>

      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          disabled={!dirty || saveMut.isPending}
          onClick={() => settingsQ.data && setDraft(settingsQ.data)}
        >
          Discard
        </Button>
        <Button
          disabled={!dirty || saveMut.isPending || !draft}
          loading={saveMut.isPending}
          onClick={() => draft && saveMut.mutate(draft)}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
